import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Button } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import ScratchersTable from './components/GamePackReport/ScratchersTable';
import PackSummary from './components/GamePackReport/PackSummary';
import ScratchersSectionNav from './components/ScratchersSectionNav';
import type { PackSummaryFilter } from './types';
import type { ScratcherGameRow } from './types';
import { useGamePackReport } from './hooks';
import { downloadCsvFile } from './utils';
import HeaderTitle from '../../components/PageHeader/HeaderTitle';
import { useQueryFilters } from '../../hooks/useQueryFilters';

type SummaryValue = {
  count: number;
  tickets: number;
  amount: number;
};

export default function Scratchers() {
  const { id } = useParams<{ id: string }>();
  const { summary, rows, packExportRows, isLoading, isError, error } = useGamePackReport(id);
  const [showMissingColumns, setShowMissingColumns] = useState(false);
  const defaultQueryFilters = useMemo(
    () => ({
      packFilter: 'all' as PackSummaryFilter | 'all',
      weekly: false,
    }),
    []
  );
  const { filters, setFilter } = useQueryFilters(defaultQueryFilters);
  const showWeeklySales = filters.weekly;
  const activeFilter = filters.packFilter === 'all' ? null : filters.packFilter;
  const validPackFilterValues = useMemo<string[]>(
    () => ['all', 'counter', 'backstock', 'notActivated', 'weekly'],
    []
  );

  const expandedBackstockSummary = useMemo<SummaryValue>(() => {
    const activeBackstock = summary.activePacksInBackstock;
    if (!showMissingColumns) {
      return activeBackstock;
    }

    return rows.reduce<SummaryValue>(
      (acc, row) => {
        const missingPacks =
          (row.backstock_missing ?? 0) + (row.backstock_missing_reconciled ?? 0);
        const missingTickets =
          (row.backstock_missing_tickets ?? 0) +
          (row.backstock_missing_reconciled_tickets ?? 0);

        acc.count += missingPacks;
        acc.tickets += missingTickets;
        acc.amount += missingTickets * (row.rate ?? 0);
        return acc;
      },
      {
        count: activeBackstock.count,
        tickets: activeBackstock.tickets,
        amount: activeBackstock.amount,
      }
    );
  }, [rows, showMissingColumns, summary.activePacksInBackstock]);

  const packFilterPredicates = useMemo<
    Record<PackSummaryFilter, (row: ScratcherGameRow) => boolean>
  >(
    () => ({
      counter: (row) =>
        (row.on_counter_count ?? 0) > 0,
      backstock: (row) =>
        (row.activated_backstock ?? 0) > 0 ||
        (showMissingColumns &&
          (((row.backstock_missing ?? 0) > 0) ||
            ((row.backstock_missing_reconciled ?? 0) > 0))),
      notActivated: (row) =>
        (row.not_activated ?? 0) > 0,
      weekly: (row) =>
        (row.weekly_sold ?? 0) > 0 && (row.weekly_sales ?? 0) > 0,
    }),
    [showMissingColumns]
  );

  const filteredRows = useMemo(() => {
    if (!activeFilter) {
      return rows;
    }

    return rows.filter(packFilterPredicates[activeFilter]);
  }, [rows, activeFilter, packFilterPredicates]);

  useEffect(() => {
    if (!validPackFilterValues.includes(filters.packFilter)) {
      setFilter('packFilter', 'all');
    }
  }, [filters.packFilter, validPackFilterValues, setFilter]);

  useEffect(() => {
    if (!showWeeklySales && activeFilter === 'weekly') {
      setFilter('packFilter', 'all');
    }
  }, [showWeeklySales, activeFilter, setFilter]);

  const visibleGameIds = useMemo(
    () => filteredRows.map((row) => row.game_id),
    [filteredRows]
  );

  const filteredPackExportRows = useMemo(() => {
    const visibleGameIdSet = new Set(visibleGameIds);
    const packMatchesActiveFilter = (row: Record<string, unknown>): boolean => {
      if (!activeFilter) {
        return true;
      }

      if (activeFilter === 'counter') {
        return (
          row.pack_location === 'display_case' ||
          row.is_in_audit === true
        );
      }

      if (activeFilter === 'backstock') {
        return (
          row.is_backstock === true ||
          row.pack_location === 'cashier' ||
          row.pack_location === 'locker' ||
          row.pack_location === 'vending' ||
          (showMissingColumns &&
            (row.backstock_state === 'missing' ||
              row.backstock_state === 'missing_reconciled'))
        );
      }

      if (activeFilter === 'notActivated') {
        return row.is_activated === false;
      }

      return true;
    };

    return packExportRows.filter((row: Record<string, unknown>) => {
      if (!visibleGameIdSet.has(String(row.game_id ?? ''))) {
        return false;
      }

      return packMatchesActiveFilter(row);
    }).sort((left: Record<string, unknown>, right: Record<string, unknown>) => {
      const leftAge = Number(left.location_age_days ?? -1);
      const rightAge = Number(right.location_age_days ?? -1);
      return rightAge - leftAge;
    });
  }, [packExportRows, activeFilter, visibleGameIds, showMissingColumns]);

  const handleDownloadCsv = () => {
    if (!filteredPackExportRows.length) {
      return;
    }

    const activeFilterLabel = activeFilter ?? 'all';
    const filename = `scratchers-pack-report-${id ?? 'store'}-${activeFilterLabel}.csv`;
    downloadCsvFile(filteredPackExportRows, filename);
  };

  return (
    <div className="scratchers-container" style={{ padding: '0px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          // marginBottom: '18px',
        }}
      >
        <HeaderTitle title="Game & Pack Report" />

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ScratchersSectionNav />
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCsv}
            disabled={isLoading || filteredPackExportRows.length === 0}
            sx={{
              borderRadius: '8px',
              borderColor: '#2c6ca0',
              color: '#2c6ca0',
              fontWeight: 600,
              fontSize: '16px',
              '&:hover': {
                borderColor: '#2c6ca0',
              },
            }}
          >
            Download CSV
          </Button>

          {activeFilter && (
            <span
              style={{
                fontWeight: 500,
                fontStyle: 'italic',
                fontSize: '16px',
                lineHeight: '100%',
                textDecorationLine: 'underline',
                cursor: 'pointer',
                color: '#2c6ca0',
              }}
              onClick={() => setFilter('packFilter', 'all')}
            >
              Reset filters
            </span>
          )}
        </div>
      </div>

      <PackSummary
        summary={summary}
        backstockSummaryOverride={expandedBackstockSummary}
        isLoading={isLoading}
        activeFilter={activeFilter}
        onFilterSelect={(value) => setFilter('packFilter', value ?? 'all')}
        isWeeklySalesEnabled={showWeeklySales}
        onWeeklySalesToggle={(checked) => setFilter('weekly', checked)}
        isMissingEnabled={showMissingColumns}
        onMissingToggle={setShowMissingColumns}
      />
      <ScratchersTable
        data={filteredRows}
        isLoading={isLoading}
        isError={isError}
        error={error}
        showWeeklySales={showWeeklySales}
        showMissingColumns={showMissingColumns}
      />
    </div>
  );
}
